/**
 * The stored shape of everything. One place, so export/import and migrations
 * have a single source of truth.
 *
 * Conventions:
 *  - `id` is a ULID-ish sortable string (see lib/id.ts).
 *  - `day` is a local-calendar date string, YYYY-MM-DD. Health data is
 *    anchored to the day the user experienced, not to UTC.
 *  - `at` is a full ISO-8601 instant with offset.
 *  - Peptide mass is always stored in MILLIGRAMS. Display converts.
 *  - Weight is always stored in KILOGRAMS. Display converts. Storing the
 *    canonical SI unit means an imported kg payload and a typed-in lb value
 *    are never silently mixed.
 */

export const SCHEMA_VERSION = 1;

export type Day = string; // YYYY-MM-DD
export type Instant = string; // ISO-8601

/** Where a value came from. Drives merge precedence; see data/biometrics.ts. */
export type Source = 'manual' | 'scale' | 'apple_health' | 'oura' | 'derived' | 'import';

// --------------------------------------------------------------------------
// Food
// --------------------------------------------------------------------------

export interface Macros {
  kcal: number;
  protein: number; // grams
  carbs: number; // grams
  fat: number; // grams
  fiber?: number;
  sugar?: number;
  sodium?: number; // mg
}

export type MealSlot = 'breakfast' | 'lunch' | 'dinner' | 'snack';

/** A food as it exists in the library: macros defined per a reference amount. */
export interface FoodItem {
  id: string;
  name: string;
  brand?: string;
  barcode?: string;
  /** Macros per `refQty` of `refUnit`. */
  macros: Macros;
  refQty: number;
  refUnit: string; // 'g' | 'ml' | 'item' | 'slice' | 'cup' ...
  /** Alternate portions, e.g. { label: 'large', grams: 63 } for an egg. */
  portions?: { label: string; qty: number; unit: string; grams?: number }[];
  source: 'core' | 'off' | 'usda' | 'custom';
  /** Upstream identifier, so a cached item can be refreshed. */
  sourceId?: string;
  /** Bumped every time the user logs it; drives the frequent-foods list. */
  useCount?: number;
  lastUsedAt?: Instant;
  createdAt: Instant;
}

/** One line in a logged meal. Macros are frozen at log time. */
export interface LoggedFood {
  foodId?: string;
  name: string;
  qty: number;
  unit: string;
  macros: Macros;
  /** True when the amount was inferred rather than measured. */
  estimated?: boolean;
}

export interface FoodLog {
  id: string;
  day: Day;
  at: Instant;
  slot: MealSlot;
  items: LoggedFood[];
  /** The text the user typed, kept so a parse can be revisited or corrected. */
  rawText?: string;
  note?: string;
}

// --------------------------------------------------------------------------
// Training
// --------------------------------------------------------------------------

export interface Exercise {
  id: string;
  name: string;
  /** Free-form grouping: 'push', 'pull', 'legs', 'core', 'other'. */
  group?: string;
  /** How a set is measured. */
  metric: 'weight_reps' | 'reps' | 'time' | 'distance';
  createdAt: Instant;
}

export interface RoutineExercise {
  exerciseId: string;
  /** Prescription, not a log: "3 x 8-10 @ RPE 8". */
  sets: number;
  repsLow?: number;
  repsHigh?: number;
  targetWeightKg?: number;
  restSec?: number;
  note?: string;
}

/** A named day within a program, e.g. "Upper A". */
export interface Routine {
  id: string;
  name: string;
  exercises: RoutineExercise[];
  createdAt: Instant;
  archivedAt?: Instant;
}

export interface Program {
  id: string;
  name: string;
  /** Ordered routine ids; the program cycles through them. */
  routineIds: string[];
  active: boolean;
  createdAt: Instant;
}

export interface LoggedSet {
  reps?: number;
  weightKg?: number;
  timeSec?: number;
  distanceM?: number;
  rpe?: number;
  /** Warm-up sets are logged but excluded from volume and PR math. */
  warmup?: boolean;
  doneAt: Instant;
}

export interface SessionExercise {
  exerciseId: string;
  sets: LoggedSet[];
}

export interface WorkoutSession {
  id: string;
  day: Day;
  startedAt: Instant;
  endedAt?: Instant;
  routineId?: string;
  programId?: string;
  name: string;
  exercises: SessionExercise[];
  note?: string;
}

// --------------------------------------------------------------------------
// Running
// --------------------------------------------------------------------------

export interface Run {
  id: string;
  day: Day;
  startedAt: Instant;
  durationSec: number;
  distanceM: number;
  /** Optional splits, one per km or mile depending on display preference. */
  avgHr?: number;
  maxHr?: number;
  activeKcal?: number;
  elevationGainM?: number;
  /** Downsampled route; full GPS tracks are not stored. */
  route?: { lat: number; lon: number; t?: number }[];
  source: Source;
  /** Upstream workout UUID, used to deduplicate re-sent Health payloads. */
  externalId?: string;
  note?: string;
}

// --------------------------------------------------------------------------
// Peptides
// --------------------------------------------------------------------------

export type Route = 'subcutaneous' | 'intramuscular' | 'oral' | 'nasal' | 'topical';

export interface Compound {
  id: string;
  name: string;
  route: Route;
  /** Injectables reconstitute; oral and nasal generally do not. */
  reconstituted: boolean;
  /** Unit the user prefers to read doses in. Storage stays mg. */
  displayUnit: 'mg' | 'mcg';
  color?: string;
  active: boolean;
  note?: string;
  createdAt: Instant;
}

export interface Vial {
  id: string;
  compoundId: string;
  label?: string;
  /** Peptide mass in the vial as labelled, mg. */
  contentMg: number;
  /** Diluent added, mL. Null until reconstituted. */
  diluentMl: number | null;
  reconstitutedAt?: Instant;
  openedAt?: Instant;
  /**
   * Beyond-use date. ALWAYS user-entered. The app does not assert how long
   * any compound remains usable -- it only counts down to the date the user
   * set.
   */
  beyondUseDate?: Day;
  storage?: 'freezer' | 'fridge' | 'room';
  /** Volume lost to pen priming per dose, mL. */
  primeLossMl?: number;
  status: 'sealed' | 'active' | 'spent' | 'discarded';
  createdAt: Instant;
}

export interface DoseLog {
  id: string;
  compoundId: string;
  vialId?: string;
  day: Day;
  at: Instant;
  /** Dose delivered, mg. For a syringe draw this is the DELIVERED mass. */
  doseMg: number;
  /** What was actually drawn, when it was an injection. */
  units?: number;
  volumeMl?: number;
  syringeId?: string;
  site?: string;
  route: Route;
  /** Volume discarded priming the pen for this dose, mL. */
  primeLossMl?: number;
  /** True when the scheduled dose was deliberately skipped. */
  skipped?: boolean;
  note?: string;
}

export type ScheduleKind = 'daily' | 'weekly' | 'interval' | 'asneeded';

export interface Protocol {
  id: string;
  compoundId: string;
  doseMg: number;
  kind: ScheduleKind;
  /** weekly: 0=Sunday..6=Saturday. */
  weekdays?: number[];
  /** interval: every N days. */
  intervalDays?: number;
  /** daily: how many times a day. */
  timesPerDay?: number;
  /** When THIS schedule began (used for scheduling arithmetic). */
  startDay: Day;
  endDay?: Day;
  /**
   * When the compound itself was started, independent of any logged dose and
   * of this schedule's startDay. Anchors before/after comparisons. Set from
   * the user's own record; a compound taken before the app existed has a
   * start date long before its first logged dose.
   */
  startedOn?: Day;
  /** Injection sites cycled through, in order. */
  sites?: string[];
  active: boolean;
  createdAt: Instant;
}

// --------------------------------------------------------------------------
// Body and subjective state
// --------------------------------------------------------------------------

/** A single measured value plus where it came from, so merges are explicit. */
export interface Reading<T = number> {
  value: T;
  source: Source;
  at: Instant;
}

/**
 * One row per calendar day. Fields arrive from different places at different
 * times; each carries its own provenance so a later Oura sync can never
 * overwrite a number the user typed.
 */
export interface DailyMetrics {
  day: Day;
  weightKg?: Reading;
  bodyFatPct?: Reading;
  leanMassKg?: Reading;
  restingHr?: Reading;
  hrvMs?: Reading;
  sleepScore?: Reading;
  readinessScore?: Reading;
  activityScore?: Reading;
  totalSleepSec?: Reading;
  remSleepSec?: Reading;
  deepSleepSec?: Reading;
  tempDeviationC?: Reading;
  steps?: Reading;
  activeKcal?: Reading;
  spo2Pct?: Reading;
  updatedAt: Instant;
}

/** Subjective 0-10 self-report. The GI field is the point of this store. */
export const GI_TAGS = ['bloating', 'cramping', 'urgency', 'nausea', 'reflux', 'constipation'] as const;
export type GiTag = (typeof GI_TAGS)[number];

export interface CheckIn {
  day: Day;
  /** 0 = awful, 10 = settled. Severity is inverted on purpose so that every
   *  0-10 field in the check-in reads the same way: higher is better. */
  gi?: number;
  /** What kind of GI trouble, when there was some. */
  giTags?: GiTag[];
  energy?: number;
  hunger?: number;
  cravings?: number;
  mood?: number;
  sleepQuality?: number;
  note?: string;
  updatedAt: Instant;
}

// --------------------------------------------------------------------------
// Settings and sync
// --------------------------------------------------------------------------

export interface Settings {
  weightUnit: 'kg' | 'lb';
  distanceUnit: 'km' | 'mi';
  energyUnit: 'kcal';
  defaultSyringeId: string;
  /** Daily macro goals. Null means untracked. */
  goals: {
    kcal?: number;
    protein?: number;
    carbs?: number;
    fat?: number;
    weightKg?: number;
    steps?: number;
  };
  sync: {
    /** Base URL of the Worker, e.g. https://reta.benops.dev */
    endpoint?: string;
    /** Shared token for the health inbox and Oura proxy. */
    token?: string;
    ouraEnabled: boolean;
    healthEnabled: boolean;
    lastHealthSyncAt?: Instant;
    lastOuraSyncAt?: Instant;
  };
  /**
   * Facts the user recorded about themselves in the previous Reta, carried
   * across on import. Shown in Settings; nothing in the app acts on them.
   */
  profile?: {
    name?: string;
    startWeightKg?: number;
    startedOn?: string;
    allergies?: string[];
    eatingWindow?: { start: string; end: string };
    timezone?: string;
  };
  /** Optional natural-language parsing assist. Off unless configured. */
  llm: {
    enabled: boolean;
    /** OpenAI-compatible chat-completions URL, or the Anthropic messages URL. */
    baseUrl?: string;
    apiKey?: string;
    model?: string;
    provider?: 'anthropic' | 'openai-compatible';
  };
  updatedAt: Instant;
}

export const DEFAULT_SETTINGS: Settings = {
  weightUnit: 'lb',
  distanceUnit: 'mi',
  energyUnit: 'kcal',
  defaultSyringeId: 'u100-0.3',
  goals: {},
  sync: { ouraEnabled: false, healthEnabled: false },
  llm: { enabled: false },
  updatedAt: '1970-01-01T00:00:00.000Z',
};

/** A raw ingested payload, kept briefly so a parse can be re-run or debugged. */
export interface RawPayload {
  id: string;
  at: Instant;
  kind: 'apple_health' | 'oura';
  /** Metric names seen that the parser did not recognise. */
  unmapped?: string[];
  body: unknown;
}

export interface StoreMap {
  foods: FoodItem;
  foodLogs: FoodLog;
  exercises: Exercise;
  routines: Routine;
  programs: Program;
  sessions: WorkoutSession;
  runs: Run;
  compounds: Compound;
  vials: Vial;
  doses: DoseLog;
  protocols: Protocol;
  metrics: DailyMetrics;
  checkins: CheckIn;
  raw: RawPayload;
}

export type StoreName = keyof StoreMap;
